import React, { useMemo, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import {
  usePropertyUnits,
  PropertyUnitGroupResponse,
  PropertyUnitLockResponse,
  PropertyUnitPersonResponse,
} from "./hooks/usePropertyData";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { SortableTableHead } from "components/shared/SortableTableHead";

const DEFAULT_PAGE_SIZE = 10;

type SortField = "unitNumber" | "guestCount" | "staffCount" | "groupCount" | "lockCount";
type SortDirection = "asc" | "desc";

function personName(person: PropertyUnitPersonResponse) {
  return `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim() || "Unknown";
}

function PeopleNameCell({ people }: { people: PropertyUnitPersonResponse[] }) {
  if (!people || people.length === 0) {
    return <span>-</span>;
  }

  const [first, ...rest] = people;

  return (
    <span>
      {personName(first)}
      {rest.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="ml-1 cursor-default underline decoration-dotted" tabIndex={0}>
              +{rest.length}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div>
              {rest.map((person) => (
                <div key={person.id}>{personName(person)}</div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}

function GroupsCell({ groups }: { groups: PropertyUnitGroupResponse[] }) {
  if (!groups || groups.length === 0) {
    return <span>-</span>;
  }

  const [first, ...rest] = groups;

  return (
    <span>
      <RouterLink to={`/admin/accessControlGroups/${first.id}`}>
        {first.name ?? "-"}
      </RouterLink>
      {rest.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="ml-1 cursor-default underline decoration-dotted" tabIndex={0}>
              +{rest.length}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div>
              {rest.map((group) => (
                <div key={group.id}>
                  <RouterLink
                    to={`/admin/accessControlGroups/${group.id}`}
                    style={{ color: "inherit" }}
                  >
                    {group.name ?? "-"}
                  </RouterLink>
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}

function LocksCell({ locks }: { locks: PropertyUnitLockResponse[] }) {
  if (!locks || locks.length === 0) {
    return <span>-</span>;
  }

  const [first, ...rest] = locks;

  return (
    <span>
      {first.name ?? "-"}
      {rest.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="ml-1 cursor-default underline decoration-dotted" tabIndex={0}>
              +{rest.length}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div>
              {rest.map((lock) => (
                <div key={lock.id}>{lock.name ?? "-"}</div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}

export default function PropertyUnits() {
  const { externalId } = useParams<{ externalId: string }>();
  const { data, isLoading, isError } = usePropertyUnits(externalId!);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("unitNumber");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(0);
  };

  // Filter, sort, and paginate data
  const filteredAndSortedData = useMemo(() => {
    if (!data) return [];

    // Fuzzy search filter
    let filtered = data;
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = data.filter((unit) =>
        (unit.unitNumber ?? "").toLowerCase().includes(searchLower),
      );
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "unitNumber":
          comparison = (a.unitNumber ?? "").localeCompare(b.unitNumber ?? "", undefined, {
            numeric: true,
          });
          break;
        case "guestCount":
          comparison = (a.guests?.length ?? 0) - (b.guests?.length ?? 0);
          break;
        case "staffCount":
          comparison = (a.staff?.length ?? 0) - (b.staff?.length ?? 0);
          break;
        case "groupCount":
          comparison = (a.groups?.length ?? 0) - (b.groups?.length ?? 0);
          break;
        case "lockCount":
          comparison = (a.locks?.length ?? 0) - (b.locks?.length ?? 0);
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [data, search, sortField, sortDirection]);

  const paginatedData = useMemo(() => {
    const start = page * pageSize;
    const end = start + pageSize;
    return filteredAndSortedData.slice(start, end);
  }, [filteredAndSortedData, page, pageSize]);

  const totalPages = useMemo(() => {
    return Math.ceil(filteredAndSortedData.length / pageSize);
  }, [filteredAndSortedData, pageSize]);

  return (
    <AdminTable
      aria-label="Property units"
      toolbar={
        <div className="px-4 py-4">
          <label className="grid w-full gap-1 text-xs font-semibold tracking-wide uppercase text-slate-500 sm:max-w-sm">
            Search units
            <input
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              placeholder="Search by unit number"
            />
          </label>
        </div>
      }
      footer={
        !isLoading && !isError && totalPages > 1 ? (
          <AdminTablePagination
            currentPage={page}
            pageSize={pageSize}
            visibleRowCount={paginatedData.length}
            disableNext={page >= totalPages - 1}
            onNext={() => setPage((currentPage) => currentPage + 1)}
            onPrevious={() => setPage((currentPage) => Math.max(0, currentPage - 1))}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
          />
        ) : undefined
      }
    >
      <thead>
        <tr>
          <SortableTableHead
            active={sortField === "unitNumber"}
            ariaLabel="Sort by unit number"
            direction={sortField === "unitNumber" ? sortDirection : "asc"}
            onSort={() => handleSort("unitNumber")}
            scope="col"
          >
            Unit
          </SortableTableHead>
          <SortableTableHead
            active={sortField === "guestCount"}
            ariaLabel="Sort by resident count"
            direction={sortField === "guestCount" ? sortDirection : "asc"}
            onSort={() => handleSort("guestCount")}
            scope="col"
          >
            Residents
          </SortableTableHead>
          <SortableTableHead
            active={sortField === "staffCount"}
            ariaLabel="Sort by staff count"
            direction={sortField === "staffCount" ? sortDirection : "asc"}
            onSort={() => handleSort("staffCount")}
            scope="col"
          >
            Staff (Temporary Access)
          </SortableTableHead>
          <SortableTableHead
            active={sortField === "groupCount"}
            ariaLabel="Sort by group count"
            direction={sortField === "groupCount" ? sortDirection : "asc"}
            onSort={() => handleSort("groupCount")}
            scope="col"
          >
            Groups
          </SortableTableHead>
          <SortableTableHead
            active={sortField === "lockCount"}
            ariaLabel="Sort by lock count"
            direction={sortField === "lockCount" ? sortDirection : "asc"}
            onSort={() => handleSort("lockCount")}
            scope="col"
          >
            Locks
          </SortableTableHead>
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr>
            <td colSpan={5}>
              <div className="flex items-center justify-center min-h-80 text-sm text-slate-500">
                Loading units...
              </div>
            </td>
          </tr>
        ) : isError ? (
          <tr>
            <td colSpan={5}>
              <div className="flex items-center justify-center px-4 text-sm text-center min-h-80 text-rose-700">
                Error loading units.
              </div>
            </td>
          </tr>
        ) : !data || data.length === 0 ? (
          <tr>
            <td colSpan={5} className="py-16 text-sm text-center text-slate-500">
              No units found for this property.
            </td>
          </tr>
        ) : filteredAndSortedData.length === 0 ? (
          <tr>
            <td colSpan={5} className="py-16 text-sm text-center text-slate-500">
              No units match your search.
            </td>
          </tr>
        ) : (
          paginatedData.map((unit) => (
            <tr key={unit.unitNumber}>
              <td>{unit.unitNumber}</td>
              <td>
                <PeopleNameCell people={unit.guests} />
              </td>
              <td>
                <PeopleNameCell people={unit.staff} />
              </td>
              <td>
                <GroupsCell groups={unit.groups} />
              </td>
              <td>
                <LocksCell locks={unit.locks} />
              </td>
            </tr>
          ))
        )}
      </tbody>
    </AdminTable>
  );
}

